import { Ball, SimulationConfig } from '@/types';
import { BALL_VERTEX_SHADER, BALL_FRAGMENT_SHADER } from './shaders';
import { WebGLContext, MAX_BALLS } from './WebGLContext';
import { WebGLUtils } from './WebGLUtils';

export class BallRenderer {
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private posBuffer: WebGLBuffer;
  private radiusBuffer: WebGLBuffer;
  private colorBuffer: WebGLBuffer;
  private velBuffer: WebGLBuffer;
  private angleBuffer: WebGLBuffer;
  private alphaBuffer: WebGLBuffer;
  private posData: Float32Array;
  private radiusData: Float32Array;
  private colorData: Float32Array;
  private velData: Float32Array;
  private angleData: Float32Array;
  private alphaData: Float32Array;

  constructor(private context: WebGLContext) {
    const gl = context.gl;
    this.program = WebGLUtils.createProgram(gl, BALL_VERTEX_SHADER, BALL_FRAGMENT_SHADER);
    this.posData = new Float32Array(MAX_BALLS * 2);
    this.radiusData = new Float32Array(MAX_BALLS);
    this.colorData = new Float32Array(MAX_BALLS * 3);
    this.velData = new Float32Array(MAX_BALLS * 2);
    this.angleData = new Float32Array(MAX_BALLS);
    this.alphaData = new Float32Array(MAX_BALLS);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    gl.bindBuffer(gl.ARRAY_BUFFER, context.geoBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.posBuffer = context.createAttribBuffer(1, 2, this.posData.byteLength);
    this.radiusBuffer = context.createAttribBuffer(2, 1, this.radiusData.byteLength);
    this.colorBuffer = context.createAttribBuffer(3, 3, this.colorData.byteLength);
    this.velBuffer = context.createAttribBuffer(4, 2, this.velData.byteLength);
    this.angleBuffer = context.createAttribBuffer(5, 1, this.angleData.byteLength);
    this.alphaBuffer = context.createAttribBuffer(6, 1, this.alphaData.byteLength);
    
    this.context.bindVertexArray(null);
  }

  render(balls: Ball[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
    if (balls.length === 0) return;
    const gl = this.context.gl;
    this.context.useProgram(this.program);
    this.context.bindVertexArray(this.vao);

    WebGLUtils.updateCommonUniforms(gl, this.program, this.context.width, this.context.height, camera, zoomLevel);

    this.updateUniforms(config);

    let activeCount = 0;
    for (let i = 0; i < Math.min(balls.length, MAX_BALLS); i++) {
        const b = balls[i];
      if (b.isBreaking) continue;

      this.posData[activeCount * 2] = b.pos.x;
      this.posData[activeCount * 2 + 1] = b.pos.y;
      this.radiusData[activeCount] = b.radius;
      WebGLUtils.parseColor(b.color, this.colorData, activeCount * 3);
      this.velData[activeCount * 2] = b.vel.x;
      this.velData[activeCount * 2 + 1] = b.vel.y;
      this.angleData[activeCount] = b.angle || 0;
      this.alphaData[activeCount] = 1.0;
      activeCount++;
    }

    this.uploadData(activeCount);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, activeCount);
    this.context.bindVertexArray(null);
  }

  private updateUniforms(config: SimulationConfig) {
    const gl = this.context.gl;
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_pulseSpeed'), config.ballPulseSpeed || 0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_squashFactor'), config.ballSquashStretch ? (config.ballSquashFactor || 0) : 0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'i_borderWidth'), config.ballStrokeWidth || 0);
    
    const bCol = WebGLUtils.hexToRgb(config.ballStrokeColor || '#000000');
    gl.uniform3f(gl.getUniformLocation(this.program, 'u_borderColor'), bCol[0]/255, bCol[1]/255, bCol[2]/255);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_strokeOpacity'), config.ballStrokeOpacity !== undefined ? config.ballStrokeOpacity : 1.0);

    const styleMap: Record<string, number> = { 
      'SHINY': 0, 'NEON': 1, 'CHROME': 2, 'MATTE': 3, 'GHOST': 4,
      'CRYSTAL': 5, 'METAL': 6, 'GLOW': 7, 'WIREFRAME': 8, 'HOLOGRAPHIC': 9, 'PLASMA': 10, 'FROSTED': 11
    };
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_ballStyle'), styleMap[config.ballStyle] || 0);

    const textureMap: Record<string, number> = {
      'NONE': 0, 'STRIPES': 1, 'DOTS': 2, 'CROSSHATCH': 3, 'NOISE': 4,
      'CHECKERBOARD': 5, 'HONEYCOMB': 6, 'CIRCUIT': 7, 'WAVES': 8, 'STARS': 9, 'BRICKS': 10, 'SWIRL': 11
    };
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_textureType'), textureMap[config.ballTexture] || 0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_textureOpacity'), config.ballTextureOpacity !== undefined ? config.ballTextureOpacity : 0.5);

    gl.uniform1f(gl.getUniformLocation(this.program, 'u_glowAmount'), config.ballGlowRadius || 0);
    const gCol = WebGLUtils.hexToRgb(config.ballGlowColor || '#ffffff');
    gl.uniform3f(gl.getUniformLocation(this.program, 'u_glowColor'), gCol[0]/255, gCol[1]/255, gCol[2]/255);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_glowOpacity'), config.ballGlowOpacity !== undefined ? config.ballGlowOpacity : 1.0);
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_fillOpacity'), config.ballFillOpacity !== undefined ? config.ballFillOpacity : 1.0);
  }

  private uploadData(count: number) {
    const gl = this.context.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.posData.subarray(0, count * 2));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.radiusBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.radiusData.subarray(0, count));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.colorData.subarray(0, count * 3));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.velBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.velData.subarray(0, count * 2));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.angleBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.angleData.subarray(0, count));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.alphaData.subarray(0, count));
  }
}
